import { z } from "zod";

import { API_DOMAIN_REGEX } from "@shared/constants";
import { Address, EVMChainConfig, TransactionReceipt, TransactionRequest } from "@shared/types";
import { InputsAssetFilters, TypedData, IPAddressOverride, Feature } from "../paymentsApiClient/types";

const WindowType = z.enum(["EMBED", "POPUP", "MODAL"]);
export type WindowType = z.infer<typeof WindowType>;

// may add capitalize later
const TextMode = z.enum(["DEFAULT", "ALL_UPPERCASE"]);
export type TextMode = z.infer<typeof TextMode>;

const FontWeight = z.enum(["AUTO", "MEDIUM"]);
export type FontWeight = z.infer<typeof FontWeight>;

const PaymentCategoryGrouping = z.enum(["ATTACHED", "BUTTON"]);
export type PaymentCategoryGrouping = z.infer<typeof PaymentCategoryGrouping>;

export const BorderStyle = z.enum(["SQUARE", "DEFAULT"]);
export type BorderStyle = z.infer<typeof BorderStyle>;

export const BackgroundStyle = z.enum(["BLUR", "OFF"]);
export type BackgroundStyle = z.infer<typeof BackgroundStyle>;

const CssColor = z.string().refine(
    (val) => {
        // Regular expression for matching CSS color formats (hex, rgb, rgba)
        const cssColorRegex =
            /^(#[0-9A-Fa-f]{3,8}|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)|rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3}(\s*,\s*(0(\.\d+)?|1(\.0+)?))\))$/;
        return cssColorRegex.test(val);
    },
    {
        message: "Invalid CSS color value",
    },
);

export const CssFontSize = z.string().refine(
    (val) => /^\d*\.?\d+px$/.test(val),
    { message: "Invalid CSS font size value. Expected a number followed by px (e.g. '16px')." },
);
export type CssFontSize = z.infer<typeof CssFontSize>;

export const CssBorderRadius = z.string().refine(
    (val) => /^\d*\.?\d+(px|rem)$/.test(val),
    { message: "Invalid CSS border radius value. Expected a number followed by px or rem (e.g. '8px', '0.5rem')." },
);
export type CssBorderRadius = z.infer<typeof CssBorderRadius>;

const Logo = z.object({
    src: z.url(),
    alt: z.string(),
    width: z.number().positive(),
    height: z.number().positive(),
});

export const CustomStyles = z.object({
    primaryColor: CssColor.optional(),
    backgroundColor: CssColor.optional(),
    borderColor: CssColor.optional(),
    textColor: CssColor.optional(),
    textSecondaryColor: CssColor.optional(),
    accentColor: CssColor.optional(),
    componentShadow: z.string().optional(),
    borderStyle: BorderStyle.optional(),
    textMode: TextMode.optional(),
    paymentCategoryGrouping: PaymentCategoryGrouping.optional(),
    fontWeight: FontWeight.optional(),
    baseFontSize: CssFontSize.optional(),
    baseBorderRadius: CssBorderRadius.optional(),
    backgroundStyle: BackgroundStyle.optional(),
    logo: Logo.optional(),
});

export type CustomStyles = z.infer<typeof CustomStyles>;

export const OrderStatus = z.any(); // TODO: Define the type for the order status
export type OrderStatus = z.infer<typeof OrderStatus>;

export const PaymentCategoryOrder = z.array(
    z.enum(["wallet", "open-wallet", "exchange", "open-exchange", "cash", "open-cash", "deposit"]),
);
export type PaymentCategoryOrder = z.infer<typeof PaymentCategoryOrder>;

export const FontName = z.enum(["haffer", "wudoo-mono", "inter"]);
export type FontName = z.infer<typeof FontName>;

export const HeaderTitle = z.string().optional();
export type HeaderTitle = z.infer<typeof HeaderTitle>;

// Function types
export type SignMessage = (input: { message: string; ownerAddress?: Address }) => Promise<string>;
export type SignTypedData = (input: { typedData: TypedData; ownerAddress?: Address }) => Promise<string>;

export type SendTransaction = (
    transaction: TransactionRequest,
    chainConfig: EVMChainConfig,
) => Promise<TransactionReceipt>;

export type WalletActionsType = {
    getAddress: () => Promise<Address>;
    signMessage?: SignMessage;
    sendTransaction?: SendTransaction;
    signTypedData?: SignTypedData;
};

export type StatusCallback = (input: { type: string; payload: OrderStatus }) => void;

const Role = z.object({
    address: Address,
    signMessage: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    sendTransaction: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    signTypedData: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
});

export type Role = z.infer<typeof Role>;

export const Redirects = z
    .object({
        redirectUrl: z.url(),
        ctaText: z.string(),
        secondaryRedirectUrl: z.url().optional(),
        secondaryCtaText: z.string().optional(),
    })
    .refine(
        (data) => {
            if (
                (data.secondaryRedirectUrl && !data.secondaryCtaText) ||
                (!data.secondaryRedirectUrl && data.secondaryCtaText)
            ) {
                return false;
            }
            return true;
        },
        {
            message: "secondaryCtaText and secondaryRedirectUrl must be provided together.",
        },
    );

// Payments Widget params
const PaymentsWidgetSDKParamsBase = z.object({
    apiKey: z.string().nonempty(),
    destinationAddress: Address.optional(),
    widgetVersion: z.enum(["1", "2"]).optional(),

    // inputs filters
    ...InputsAssetFilters.shape,

    // widget display
    customStyles: CustomStyles.optional(),
    targetElementId: z.string().optional(),
    windowType: WindowType.optional(),

    // redirects
    redirects: Redirects.optional(),

    // functions
    statusCallback: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),

    // roles
    owner: Role.optional(),
    funder: Role.optional(),

    // features
    features: z.array(Feature).optional(),
    paymentCategoryOrder: PaymentCategoryOrder.optional(),
    fontName: FontName.optional(),
    headerTitle: HeaderTitle.optional(),

    /** Optional array of onramp provider names to filter/display in the widget */
    onramps: z.array(z.string()).optional(),

    // Skeleton options (EMBED mode)
    /** (EMBED only) Show a loading skeleton while widget loads (default: true) */
    showSkeleton: z.boolean().optional(),
    /** (EMBED only) Custom skeleton background color */
    skeletonBackgroundColor: z.string().optional(),
});

export const PaymentsWidgetSDKParams = PaymentsWidgetSDKParamsBase.refine(
    (data) => {
        if (data.windowType === "EMBED" && !data.targetElementId) {
            return false;
        }
        return true;
    },
    {
        message: "targetElementId is required when windowType is EMBED",
    },
);

const PaymentsWidgetSDKParamsWithoutRolesAndFunctions = PaymentsWidgetSDKParamsBase.omit({
    owner: true,
    funder: true,
    statusCallback: true,
});

export type PaymentsWidgetSDKParams = z.input<typeof PaymentsWidgetSDKParamsWithoutRolesAndFunctions> & {
    owner?: Omit<WalletActionsType, "getAddress"> & { address: Address };
    funder?: Omit<WalletActionsType, "getAddress"> & { address: Address };
    statusCallback?: StatusCallback;
};

// Accepted overrides
const apiBaseUrl = z.string().regex(API_DOMAIN_REGEX, "Invalid API baseUrl");
// Make it a record of strings to booleans for flexibility
const featureFlags = z.record(z.string(), z.boolean());

export const SDKOverrides = z.object({
    dangerouslyOverrideHallidayDomainName: z.url().optional(),
    dangerouslyOverrideApiBaseUrl: apiBaseUrl.optional(),
    dangerouslyOverrideIPAddress: IPAddressOverride.optional(),
    dangerouslyOverrideFeatureFlags: featureFlags.optional(),
});

export type SDKOverrides = z.infer<typeof SDKOverrides>;

export const AppMode = z.enum(["FULL", "MODAL", "OVERLAY", "EMBED"]);
export type AppMode = z.infer<typeof AppMode>;

// Additional data computed by the SDK
export const SDKAdditions = z.object({
    appMode: AppMode.optional(),
    apiBaseUrl: apiBaseUrl.optional(),
    hasOwner: z.boolean(),
    hasTxHandler: z.boolean(),
    hostOrigin: z.url().nullable(),
    ipAddress: IPAddressOverride.optional(),
    featureFlags: featureFlags.optional(),
});

// QueryParams passed to the widget
export const PaymentsWidgetQueryParams = z.object({
    // omit all functions and roles
    ...PaymentsWidgetSDKParamsWithoutRolesAndFunctions.shape,
    // add all additions
    ...SDKAdditions.shape,
    // Add ownerAddress if owner is provided
    ownerAddress: Address.optional(),
    funderAddress: Address.optional(),
});
export type PaymentsWidgetQueryParams = z.infer<typeof PaymentsWidgetQueryParams>;

// Message action types
export enum MessageType {
    ACTION_TRANSACTION = "ACTION_TRANSACTION",
    EVENT_ORDER_STATUS = "EVENT_ORDER_STATUS",
    EVENT_WINDOW_CLOSE = "EVENT_WINDOW_CLOSE",
    ACTION_SIGN_MESSAGE = "ACTION_SIGN_MESSAGE",
    ACTION_PROVIDER_WIDGET = "ACTION_PROVIDER_WIDGET",
    ACTION_SIGN_TYPED_DATA = "ACTION_SIGN_TYPED_DATA",
    EVENT_RESIZE = "EVENT_RESIZE",
}

export type Message =
    | {
          type: MessageType.ACTION_TRANSACTION;
          payload: { transaction: TransactionRequest; messageId: string; chainConfig: EVMChainConfig };
      }
    | { type: MessageType.EVENT_ORDER_STATUS; payload: OrderStatus }
    | { type: MessageType.EVENT_WINDOW_CLOSE; payload: undefined }
    | {
          type: MessageType.ACTION_SIGN_MESSAGE;
          payload: { message: string; messageId: string; ownerAddress?: Address };
      }
    | {
          type: MessageType.ACTION_PROVIDER_WIDGET;
          payload: {
              providerWidgetMode: "REDIRECT" | "POPUP";
              redirectUrl: string;
              url: string;
              workflowId: string;
          };
      }
    | {
          type: MessageType.ACTION_SIGN_TYPED_DATA;
          payload: { typedData: TypedData; messageId: string; ownerAddress?: Address };
      }
    | {
          type: MessageType.EVENT_RESIZE;
          payload: { width?: number; height?: number };
      };

export type MessageResponse =
    | {
          type: MessageType.ACTION_TRANSACTION;
          payload:
              | { messageId: string; txReceipt: TransactionReceipt; error: null }
              | { messageId: string; txReceipt: null; error: string };
      }
    | {
          type: MessageType.ACTION_SIGN_MESSAGE;
          payload:
              | { messageId: string; signature: string; error: null }
              | { messageId: string; signature: null; error: string };
      }
    | {
          type: MessageType.ACTION_SIGN_TYPED_DATA;
          payload:
              | { messageId: string; signature: string; error: null }
              | { messageId: string; signature: null; error: string };
      };

// ============================================
// Preload Types
// ============================================

/**
 * Configuration for initializeClient.
 *
 * This is intentionally minimal - init is just for preloading.
 * All business logic params (outputs, customStyles, etc.) go in openHallidayPayments.
 */
export const InitConfig = z.object({
    /** Your API key for authorization (required) */
    apiKey: z.string().nonempty(),

    /** Whether the widget is in sandbox mode (default: false) */
    sandbox: z.boolean().optional(),

    /** Callback when widget is preloaded and ready */
    onReady: z
        .any()
        .refine((val) => typeof val === "function" || val === undefined)
        .optional(),

    /** Callback if preloading fails */
    onError: z
        .any()
        .refine((val) => typeof val === "function" || val === undefined)
        .optional(),
});

export type InitConfig = z.input<typeof InitConfig> & {
    onReady?: () => void;
    onError?: (error: Error) => void;
};
